import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  Pressable,
  ActivityIndicator,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../constants/config';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { kpisApi } from '../services/apiService';

interface KPIDeleteConfirmModalProps {
  visible: boolean;
  kpiId: number | null;
  kpiName?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

interface ImpactCounts {
  readings: number;
  schedules: number;
}

const WarningIcon = ({ size = 48, color = COLORS.danger }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path d="M12 8V12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M12 16H12.01" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

export default function KPIDeleteConfirmModal({
  visible,
  kpiId,
  kpiName,
  onConfirm,
  onCancel,
}: KPIDeleteConfirmModalProps) {
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();
  const [impact, setImpact] = useState<ImpactCounts | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const isExisting = typeof kpiId === 'number' && kpiId > 0;

  useEffect(() => {
    if (!visible) {
      setImpact(null);
      return;
    }
    if (!isExisting || kpiId === null) {
      setImpact({ readings: 0, schedules: 0 });
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setImpact(null);

    Promise.all([
      kpisApi.getReadings(kpiId).catch(() => [] as unknown[]),
      kpisApi.getSchedules(kpiId).catch(() => [] as unknown[]),
    ])
      .then(([readings, schedules]) => {
        if (cancelled) return;
        setImpact({
          readings: Array.isArray(readings) ? readings.length : 0,
          schedules: Array.isArray(schedules) ? schedules.length : 0,
        });
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [visible, kpiId, isExisting]);

  const themeColors = {
    container: colors.card,
    title: colors.textPrimary,
    message: colors.textMuted,
    cancelButton: isDark ? colors.backgroundAlt : COLORS.neutral100,
    cancelButtonText: colors.textPrimary,
    impactBg: isDark ? colors.backgroundAlt : '#FEF2F2',
    impactBorder: isDark ? colors.border : '#FECACA',
    impactText: colors.textPrimary,
  };

  const totalImpact = (impact?.readings ?? 0) + (impact?.schedules ?? 0);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onCancel}>
      <Pressable style={styles.overlay} onPress={onCancel}>
        <Pressable
          style={[styles.container, { backgroundColor: themeColors.container }]}
          onPress={(e) => e.stopPropagation()}
        >
          <View style={[styles.iconContainer, { backgroundColor: `${COLORS.danger}15` }]}>
            <WarningIcon size={48} color={COLORS.danger} />
          </View>

          <Text style={[styles.title, { color: themeColors.title }]}>
            {t('kpis.deleteConfirmTitle', 'حذف المؤشر')}
          </Text>

          {kpiName ? (
            <Text style={[styles.kpiName, { color: themeColors.title }]} numberOfLines={2}>
              « {kpiName} »
            </Text>
          ) : null}

          <Text style={[styles.message, { color: themeColors.message }]}>
            {t('kpis.deleteWarning', 'سيتم حذف هذا المؤشر نهائياً ولا يمكن استرجاعه.')}
          </Text>

          {isExisting ? (
            <View style={[styles.impactBox, { backgroundColor: themeColors.impactBg, borderColor: themeColors.impactBorder }]}>
              {isLoading ? (
                <View style={styles.loadingRow}>
                  <ActivityIndicator size="small" color={COLORS.danger} />
                  <Text style={[styles.impactLoadingText, { color: themeColors.message }]}>
                    {t('common.loading', 'جاري التحميل...')}
                  </Text>
                </View>
              ) : impact && totalImpact > 0 ? (
                <>
                  <Text style={[styles.impactHeader, { color: themeColors.impactText }]}>
                    {t('kpis.deleteImpactHeader', 'سيتم حذف البيانات المرتبطة:')}
                  </Text>
                  {impact.readings > 0 && (
                    <Text style={[styles.impactItem, { color: themeColors.impactText }]}>
                      • {t('kpis.readingsCount', '{{count}} قراءة', { count: impact.readings })}
                    </Text>
                  )}
                  {impact.schedules > 0 && (
                    <Text style={[styles.impactItem, { color: themeColors.impactText }]}>
                      • {t('kpis.schedulesCount', '{{count}} جدولة قراءة', { count: impact.schedules })}
                    </Text>
                  )}
                </>
              ) : (
                <Text style={[styles.impactItem, { color: themeColors.message }]}>
                  {t('kpis.deleteNoImpact', 'لا توجد بيانات مرتبطة بهذا المؤشر.')}
                </Text>
              )}
            </View>
          ) : null}

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.button, { backgroundColor: themeColors.cancelButton }]}
              onPress={onCancel}
              disabled={isLoading}
            >
              <Text style={[styles.cancelButtonText, { color: themeColors.cancelButtonText }]}>
                {t('common.cancel', 'إلغاء')}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.button, { backgroundColor: COLORS.danger, opacity: isLoading ? 0.6 : 1 }]}
              onPress={onConfirm}
              disabled={isLoading}
            >
              <Text style={styles.confirmButtonText}>{t('common.delete', 'حذف')}</Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  container: {
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 420,
    alignItems: 'center',
  },
  iconContainer: {
    width: 72,
    height: 72,
    borderRadius: 36,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    fontFamily: FONTS.medium,
    textAlign: 'center',
    marginBottom: 6,
  },
  kpiName: {
    fontSize: 15,
    fontFamily: FONTS.medium,
    textAlign: 'center',
    marginBottom: 10,
  },
  message: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 22,
  },
  impactBox: {
    width: '100%',
    borderRadius: 10,
    borderWidth: 1,
    padding: 12,
    marginBottom: 20,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  impactLoadingText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
  },
  impactHeader: {
    fontSize: 13,
    fontFamily: FONTS.medium,
    marginBottom: 6,
  },
  impactItem: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    lineHeight: 20,
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
  },
  button: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontFamily: FONTS.medium,
  },
  confirmButtonText: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: COLORS.white,
  },
});
